import type { CgOrder, CgOrderType, CgStatus } from './types';

/**
 * Which pipeline the CG Orders page is looking at.
 *
 * ── Why this is one page-level choice rather than a control per tab ────────
 *
 * Kyu: "the switcher should scope the whole CG Orders page, not just the board
 * and table. If Custom and In-Stock have different operating rhythms, then the
 * Dashboard / summary metrics / reporting on that page should follow the
 * selected pipeline too. I do not want blended numbers that look precise but
 * are not useful."
 *
 * That is the same failure the Sales Tracker had when unclassified leads were
 * counted as outbound: an average that mixes a 25-day factory order with a
 * 2-day warehouse ship is a number nobody can act on, and nothing on screen
 * says so.
 *
 * ── Why 'all' exists, and which tabs may offer it ──────────────────────────
 *
 * Kyu: "the Orders table should still have an All option so we can see both
 * pipelines together when needed. That gives us focused views and one combined
 * operational view." And, for Reports: "Reporting stays unified with a filter:
 * All / Custom Only / In-Stock Only."
 *
 * Orders is a flat list and Reports is rows with an optional grouping. Neither
 * has to average anything across the two pipelines, so mixing is honest there.
 * The other three cannot honour it and must not pretend to:
 *
 *   Dashboard — its KPIs would blend the two rhythms. The whole point above.
 *   Pipeline  — its COLUMNS are one ladder. Two ladders is not one board.
 *   Inventory — what a factory is holding. In-Stock orders have no factory.
 *
 * So those three fall back to Custom, and the switcher MOVES with them rather
 * than leaving an "All" label over custom-only data.
 */

export type CgPipelineScope = CgOrderType | 'all';

/** The tabs the CG Orders page is made of. Kept here so scope rules can name them. */
export type CgScopeTab = 'dashboard' | 'pipeline' | 'orders' | 'inventory' | 'reports';

export const SCOPE_LABELS: Record<CgPipelineScope, string> = {
  custom: 'Custom',
  in_stock: 'In-Stock',
  all: 'All',
};

/** Custom first: it is every order that exists today and most that will. */
const BOTH: CgPipelineScope[] = ['custom', 'in_stock'];
const WITH_ALL: CgPipelineScope[] = ['custom', 'in_stock', 'all'];

/**
 * Which choices this tab can honestly offer.
 *
 * Orders and Reports can hold both pipelines at once: one is a flat list and
 * the other is rows with an optional grouping, and neither has to average
 * anything across the two. Kyu asked for exactly that on Reports — "All /
 * Custom Only / In-Stock Only".
 *
 * Dashboard, Pipeline and Inventory still cannot, for the reasons at the top
 * of this file.
 */
const TABS_THAT_CAN_MIX: CgScopeTab[] = ['orders', 'reports'];

export function scopesForTab(tab: CgScopeTab): CgPipelineScope[] {
  return TABS_THAT_CAN_MIX.includes(tab) ? WITH_ALL : BOTH;
}

/**
 * A scope, in the form a downloaded file can carry in its name.
 *
 * An exported CSV of custom-only orders that looks like everything is the
 * blended-number problem in a file somebody will still have next quarter,
 * after the screen that made it is long gone.
 */
export function scopeFileTag(scope: CgPipelineScope): string {
  return scope === 'in_stock' ? 'in-stock' : scope;
}

/**
 * The scope this tab will actually apply.
 *
 * Falls back rather than throwing, because the fallback is what somebody sees
 * when they pick All on Orders and then click Dashboard. The switcher renders
 * from this same function, so the control moves to Custom at the same instant
 * the numbers do — there is no state in which the label and the data disagree.
 */
export function resolveScope(scope: CgPipelineScope, tab: CgScopeTab): CgPipelineScope {
  return scopesForTab(tab).includes(scope) ? scope : 'custom';
}

/**
 * Which ladder to load for this scope.
 *
 * 'all' loads BOTH, because under All the Orders table holds rows from either
 * pipeline and each row's status dropdown has to offer its own stages. Offering
 * the whole list to every row would put In-Stock stages on a Custom order,
 * which the database refuses — a dropdown whose options are rejected on click
 * is worse than one that never offered them.
 */
export function statusScopeFor(scope: CgPipelineScope): CgOrderType | 'all' {
  return scope;
}

/**
 * Which pipeline an order belongs to, for the purposes of showing it.
 *
 * Anything that is not explicitly in_stock counts as custom. The column is NOT
 * NULL with a default, so this should never fire — but the alternative when it
 * does is an order that appears under NO scope and simply vanishes from every
 * view. The board already refuses to lose an order whose status it does not
 * recognise, for the same reason: a row nobody can see is a row nobody knows
 * is wrong.
 */
function scopeOf(order: CgOrder): CgOrderType {
  return order.order_type === 'in_stock' ? 'in_stock' : 'custom';
}

export function filterOrdersByScope(orders: CgOrder[], scope: CgPipelineScope): CgOrder[] {
  if (scope === 'all') {
    return orders;
  }
  return orders.filter((order) => scopeOf(order) === scope);
}

/** The stages an order may actually move to: its own ladder, never the other. */
export function statusesForOrder(order: CgOrder, statuses: CgStatus[]): CgStatus[] {
  return statuses.filter((status) => status.order_type === scopeOf(order));
}

/**
 * What to call a stage on screen.
 *
 * Three In-Stock stage names collide with Custom ones — Order Placed, Shipped,
 * Delivered — so their ids carry a suffix and the readable name lives in
 * `label`. Everything that decides anything still works on the id.
 */
export function statusLabelOf(status: CgStatus): string {
  const label = status.label ?? '';
  return label.trim() === '' ? status.id : label;
}

/** The badge text for an order's pipeline, for the Orders table under All. */
export function orderTypeLabel(order: CgOrder): string {
  return scopeOf(order) === 'in_stock' ? 'In-Stock' : 'Custom';
}

/**
 * Whether this pipeline has a factory behind it.
 *
 * The New Order form and the quick-edit drawer both ask this rather than
 * testing the type inline, so "which fields does a stock order have" is one
 * rule with one answer instead of four comparisons that can drift apart.
 *
 * An In-Stock order is warehouse stock. There is no PO, no acknowledgement, no
 * sample clock and no build instructions for anybody — and a blank field
 * somebody thinks they ought to fill is worse than one that was never offered.
 */
export function hasSupplierStage(orderType: CgOrderType): boolean {
  return orderType === 'custom';
}
